import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr, API_BASE, getStoredToken } from "@/lib/api";
import { Camera, Plus, Trash2, Loader2, FileText, ImagePlus, CalendarDays } from "lucide-react";

const fileUrl = (path) => `${API_BASE}/files/${path}?auth=${encodeURIComponent(getStoredToken())}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function ProgressTab({ projectId }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const { data: notes = [], isLoading } = useQuery({ queryKey: ["progress", projectId], queryFn: async () => (await api.get(`/projects/${projectId}/progress-notes`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["progress", projectId] });

  const pdfUrl = `${API_BASE}/projects/${projectId}/progress-report/pdf?auth=${encodeURIComponent(getStoredToken())}`;

  const add = async () => {
    if (!note.trim() && files.length === 0) { toast.error("Add a note or a photo"); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("note", note);
    fd.append("date", date);
    files.forEach((f) => fd.append("files", f));
    try {
      await api.post(`/projects/${projectId}/progress-notes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNote(""); setFiles([]); setDate(today());
      if (fileRef.current) fileRef.current.value = "";
      refresh();
      toast.success("Progress note added");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const del = async (id) => { try { await api.delete(`/progress-notes/${id}`); refresh(); } catch (e) { toast.error(apiErr(e)); } };

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><Camera className="h-4 w-4 text-primary" /> Site Progress</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Log dated site notes and photos, then compile a shareable PDF report for the client.</p>
        </div>
        <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="progress-pdf-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 font-bold hover:bg-orange-600 transition-colors">
          <FileText className="h-4 w-4" /> Report PDF
        </a>
      </div>

      {/* Add note */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-2 rounded-lg border border-input bg-popover px-3 py-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <input data-testid="progress-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-transparent text-sm outline-none" />
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="progress-file-input"
            onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          <button data-testid="progress-attach-btn" onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-secondary transition-colors">
            <ImagePlus className="h-4 w-4" /> {files.length ? `${files.length} photo(s)` : "Attach photos"}
          </button>
        </div>
        <textarea data-testid="progress-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
          placeholder="e.g. Framing complete on level 2; drywall delivered; minor delay on plumbing rough-in due to rain."
          className="w-full rounded-lg border border-input bg-popover px-3 py-2.5 text-sm outline-none focus:border-primary resize-none" />
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {files.map((f, i) => <span key={i} className="text-xs font-mono bg-secondary rounded px-2 py-1 truncate max-w-[160px]">{f.name}</span>)}
          </div>
        )}
        <div className="flex justify-end">
          <button data-testid="add-progress-btn" onClick={add} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-secondary hover:bg-secondary/70 px-4 py-2.5 font-semibold text-sm transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Entry
          </button>
        </div>
      </div>

      {/* Timeline */}
      {notes.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-14 text-center text-muted-foreground">No progress entries yet.</div>
      ) : (
        <div className="space-y-4" data-testid="progress-timeline">
          {notes.map((n) => (
            <div key={n.id} className="rounded-xl border border-border bg-card p-4" data-testid={`progress-entry-${n.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-sm font-mono font-semibold text-primary">{n.date}</span>
                <button data-testid={`delete-progress-${n.id}`} onClick={() => del(n.id)} className="h-7 w-7 grid place-items-center rounded text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              {n.note && <p className="text-sm whitespace-pre-wrap mb-3">{n.note}</p>}
              {(n.photo_paths || []).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {n.photo_paths.map((p, i) => (
                    <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="block">
                      <img src={fileUrl(p)} alt="" className="h-28 w-full object-cover rounded-lg border border-border hover:opacity-80 transition-opacity" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
